import React, { useState, useEffect } from "react";
import moment from "moment";

const SCOPES = {
  "user:read": "Read user data",
  "user:write": "Edit user data",
  "farm:read": "Read farms",
  "farm:write": "Edit farms",
  "plan:read": "Read plans",
  "plan:write": "Edit plans",
  "parcel:read": "Read parcels",
  "parcel:write": "Edit parcels",
  "parcel_d:read": "Read parcel data",
  "parcel_d:write": "Edit parcel data",
  "barn:read": "Read barn",
  "barn:write": "Edit barn",
  "crop_f:read": "Read cropfields",
  "crop_f:write": "Edit cropfields",
  "crop_rec:read": "Read record data",
  "crop_rec:write": "Edit record data",
};
export default function PersonalToken() {
  const [tokens, setTokens] = useState(null);
  const [editToken, setEditToken] = useState(null);
  useEffect(() => {
    fetch("/apps/cropletdeveloper/personal_tokens/list/", {
      method: "get",
      credentials: "include",
    })
      .then((response) => response.json().then((tokens) => setTokens(tokens || [])))
      .catch((tokens) => setTokens([]));
  }, []);

  const createToken = async (token) => {
    let form = new FormData();
    form.append("scope", (token.scopes || []).join(" "));
    const response = await fetch("/apps/cropletdeveloper/personal_tokens/create/", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "X-CSRFToken": document.getElementsByName("csrfmiddlewaretoken")[0].value,
      },
      body: form,
    });
    if (response.ok) {
      setEditToken(null);
      let newToken = await response.json();
      setTokens([{ ...newToken, new: true }, ...tokens]);
    }
  };

  const revokeToken = (token) => {
    fetch(`/apps/cropletdeveloper/personal_tokens/revoke/${token.id}/`, {
      method: "get",
      credentials: "include",
    })
      .then(() => setTokens(tokens.filter(({ id }) => id !== token.id)))
      .catch((error) => alert("Could not revoke token!"));
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className={"d-flex flex-row justify-content-between"}>
          <h2 className="card-title">
            {editToken === null ? "Personal tokens" : editToken.id ? "Edit token" : "New token"}
          </h2>
          {editToken === null ? (
            <button
              type="button"
              className="btn btn-primary align-right"
              onClick={() =>
                setEditToken({
                  scopes: Object.keys(SCOPES).filter((key) => key.indexOf("read") >= 0),
                })
              }
            >
              Generate new token
            </button>
          ) : null}
        </div>
      </div>
      <div className="card-body">
        {editToken === null ? (
          <div>
            {tokens !== null && tokens.length ? (
              <ul className="list-group">
                {tokens.map((token) => (
                  <li key={`token${token.id}`} className={`list-group-item d-flex flex-column`}>
                    <h5>Created: {moment(token.created.replace("T", " ")).format("LLLL")}</h5>
                    <div className="d-flex flex-row justify-content-between">
                      <div className="d-flex flex-column">
                        {token.token && token.new ? (
                          <span className="alert alert-success">
                            <p>This is your token, copy this now since it wont be visible later.</p>
                            <code>{token.token}</code>
                          </span>
                        ) : null}
                        <span>Scopes: </span>
                        <ul>
                          {(token.scope || "").split(" ").map((scope) => (
                            <li>{SCOPES[scope]}</li>
                          ))}
                        </ul>
                      </div>
                      <button
                        style={{ alignSelf: "flex-end" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm("Are you sure you want to revoke this token?")) {
                            revokeToken(token);
                          }
                        }}
                        className="btn btn-danger"
                      >
                        Revoke
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : tokens === null ? (
              <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            ) : (
              <span className="alert alert-info">
                Generate an Oauth2 token for scripts or testing.
              </span>
            )}
          </div>
        ) : (
          <div>
            <h4>Scopes</h4>
            <p>
              Scopes can limit your actions on the api, configure what scopes are relevant for this
              token. Farm owners can still limit your actions on their farm, these scopes do not
              grant you more rights than you currently have.
            </p>
            <ul className="list-group">
              {Object.keys(SCOPES)
                .reduce((allScopes, scope) => {
                  const [right] = scope.split(":");
                  if (!allScopes.includes(right)) {
                    allScopes.push(right);
                  }
                  return allScopes;
                }, [])
                .map((key) => (
                  <li className="list-group-item">
                    <div className="d-flex flex-column">
                      <h5>{key}</h5>
                      {["read", "write"].map((permission) => (
                        <div key={`${key}_${permission}_container`} className="form-check">
                          <input
                            readOnly
                            className="form-check-input"
                            type="checkbox"
                            disabled={
                              permission === "read" &&
                              (editToken.scopes || []).includes(`${key}:write`)
                            }
                            checked={(editToken.scopes || []).includes(`${key}:${permission}`)}
                            onClick={() =>
                              setEditToken({
                                ...editToken,
                                scopes: (editToken.scopes || []).includes(`${key}:${permission}`)
                                  ? editToken.scopes.filter(
                                      (scope) => scope !== `${key}:${permission}`
                                    )
                                  : [
                                      ...(editToken.scopes || []),
                                      `${key}:${permission}`,
                                      ...(permission === "write" &&
                                      !(editToken.scopes || []).includes(`${key}:read`)
                                        ? [`${key}:read`]
                                        : []),
                                    ],
                              })
                            }
                            id={`scope_${key}_${permission}`}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`scope_${key}_${permission}`}
                          >
                            {SCOPES[`${key}:${permission}`]}
                          </label>
                        </div>
                      ))}
                    </div>
                  </li>
                ))}
            </ul>
            <div className="btn-group">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => createToken(editToken)}
              >
                Create personal token
              </button>
              <button type="button" className="btn btn-light" onClick={() => setEditToken(null)}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
